import { asyncAction } from "../common"
import { getState } from "storeAccess"
import { limsRequest } from "./limsRequest"
import * as limsBatchHold from "./limsBatchHold"
import * as limsOos from "./limsOos"
import { isQaApproved } from "./limsResults"

// QA RELEASE / disposition of a sample (lot + observation id) for sale (F-C4). SPEC-013 (A3): a
// sample row in the Sample View becomes releasable (READY) only once EVERY analysis planned for it
// carries a QA-APPROVED QC result (SPEC-012 `qa-approved`) and none is batch-rejected (a signed QA
// STOP). This SUPERSEDES the SPEC-011/012 interim gate (which counted any reviewer-signed result):
// release now means strictly "all QA-approved + no STOP". Release itself is a QA-signed action
// (capability "qa", e-signature; the server re-evaluates the whole gate at signing — SPEC-021).
// Persisted via the real /lims/release endpoints, keyed by the stable compound sample key
// (product|name|observationId, upsert-by-key).
const initialState = { items: [], isLoading: false }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsRelease/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsRelease/fetch":
      return { items: action.res, isLoading: false }
    case "limsRelease/release": {
      const exists = state.items.some((x) => x.id === action.res.id)
      const items = exists
        ? state.items.map((x) => (x.id === action.res.id ? action.res : x))
        : [...state.items, action.res]
      return { ...state, items }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsRelease/fetch", (_dispatch) =>
  limsRequest("GET", "/lims/release").then((res) => res.items)
)

// Stable identity for a Sample View sample group (lot name + observation id under a product).
export function sampleReleaseKey(group) {
  if (!group) return ""
  return `${group.productName || ""}|${group.sampleName || ""}|${group.observationId ?? ""}`
}

// SPEC-020: the error rejected by release() when the gate is no longer READY at signing time.
// Callers match on `code` to surface the dedicated i18n message instead of the generic failure.
export const RELEASE_NOT_READY = "RELEASE_NOT_READY"
export const isReleaseNotReadyError = (err) => !!(err && err.code === RELEASE_NOT_READY)

// Release a sample for sale. `signature` is the QA ESignatureModal payload (reason "released");
// its password + reason ride at the request-body TOP LEVEL (SPEC-021 B1) — the SERVER re-verifies
// the password, the "qa" capability and the 4-eyes exclusion, re-evaluates the whole release gate
// (BATCH_REJECTED / ON_HOLD / all-qa-approved, 422 with distinct messages) and stamps
// releasedBy/releasedAt + the immutable signature and audit entry.
//
// SPEC-020 gate revalidation (kept as fail-fast UX on top of the authoritative server gate):
// between the Release click and the signed submit, the gate may have changed (a batch hold placed,
// an OOS investigation opened, a result returned for correction). The status is re-derived from
// the CURRENT store immediately before persisting; anything other than READY aborts the release
// instead of signing blind.
export const release = asyncAction("limsRelease/release", (_dispatch, group, signature) => {
  const state = getState()
  if (selectSampleReleaseStatus(state, group) !== RELEASE_STATUS.READY) {
    const err = new Error("release aborted: the sample's release status is no longer READY")
    err.code = RELEASE_NOT_READY
    return Promise.reject(err)
  }
  const payload = {
    id: sampleReleaseKey(group),
    productName: group.productName || null,
    sampleName: group.sampleName || null,
    observationId: group.observationId ?? null,
  }
  return limsRequest("POST", "/lims/release", {
    input: payload,
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// SPEC-038 (F6): resolve a short-lived presigned GET url for a release's CERTIFICATE (the
// server-generated Certificate of Analysis stored in the qaReports S3 area at release time, keyed
// by `certificateFileKey` on the release record). Reuses the existing qaReports read-side seam.
// Not an asyncAction (touches no store state); returns the url so the caller opens it on demand —
// the url expires (~1h), so it is resolved lazily and never cached in redux. Returns null when the
// release has no stored certificate (the qaReports bucket was not provisioned at release time — the
// caller falls back to the client-rendered printable CoA).
export const certificateDownloadUrl = (release) => () => {
  const key = release && release.certificateFileKey
  if (!key) return Promise.resolve(null)
  return limsRequest("POST", "/lims/qaReports/get-read-url", { input: { key } }).then(
    (res) => res.presignedUrl
  )
}

// Whether a release record carries a downloadable server-generated certificate (vs. only the
// client-rendered printable CoA fallback).
export const hasStoredCertificate = (release) => !!(release && release.certificateFileKey)

export const selectRelease = (state, group) =>
  state.limsRelease.items.find((r) => r.id === sampleReleaseKey(group))

// SPEC-020 four-eyes on batch release: the member ids EXCLUDED from signing a group's release —
// every analyst who ENTERED one of the group's QC results, every REVIEWER who signed one off
// (signature or legacy reviewedBy mirror) and every reviewer who e-signed an okOverride. QA's
// release must be an independent set of eyes on the lot. Derived from the group's results by
// sampleId (the same safe join the release gate uses).
export function selectReleaseExclusions(state, group) {
  const sampleIds = new Set(((group && group.analyses) || []).map((a) => a.sampleId).filter(Boolean))
  const excluded = new Set()
  ;((state.limsResults && state.limsResults.items) || []).forEach((r) => {
    if (!sampleIds.has(r.sampleId)) return
    if (r.enteredBy) excluded.add(r.enteredBy)
    if (r.reviewedBy) excluded.add(r.reviewedBy)
    if (r.reviewerSignature && r.reviewerSignature.memberId) excluded.add(r.reviewerSignature.memberId)
    if (r.okOverrideSignature && r.okOverrideSignature.memberId) excluded.add(r.okOverrideSignature.memberId)
  })
  return Array.from(excluded)
}

// Release lifecycle status shown per Sample View sample row.
export const RELEASE_STATUS = {
  IN_PROGRESS: "in-progress",
  IN_REVIEW: "in-review",
  READY: "ready",
  // SPEC-018 (PRD-005 Area 4) RE-INTRODUCED this: a RECOVERABLE hold returned when a sample in the
  // group has an OPEN OOS INVESTIGATION. The constant existed since F-C4 but was made DEAD by
  // SPEC-013 (which removed the open-OOS branch because SPEC-011 stopped auto-raising OOS records);
  // SPEC-018's MANUALLY-opened investigation revives the semantic. RECOVERABLE means: close the
  // investigation (QA signature) and the hold lifts — the gate re-evaluates to READY / IN_REVIEW /
  // IN_PROGRESS. This is DISTINCT from BATCH_REJECTED below, which is a signed, TERMINAL block.
  ON_HOLD: "on-hold",
  // SPEC-010 (Area 4): a distinct, TERMINAL status for a sample whose batch/lot was REJECTED by a
  // signed QA decision — non-releasable and NOT recoverable (unlike ON_HOLD, a batch rejection
  // cannot be lifted by closing an investigation; it is a permanent block from sale).
  BATCH_REJECTED: "batch-rejected",
  RELEASED: "released",
}

// The batch/lot strings carried by a release group's samples (via their F-C1 sample-QC details).
function batchLotsForGroup(state, group) {
  const analyses = (group && group.analyses) || []
  const lots = analyses
    .map((a) => {
      const qc = a.sampleId ? state.limsSampleQc && state.limsSampleQc[a.sampleId] : null
      return qc && qc.batchLot ? qc.batchLot : null
    })
    .filter(Boolean)
  return Array.from(new Set(lots))
}

// SPEC-038 (F6): the distinct batch/lot strings registered on a sample/lot group's sample-analyses
// (the F-C1 sample-QC `batchLot`). Exposed so the QA SampleReview "Reject" action can place a batch
// hold on the lot — a lot-level rejection blocks the whole batch from sale. Empty when no lot is
// registered (the screen then blocks Reject and asks QA to register the lot first).
export const selectGroupBatchLots = (state, group) => batchLotsForGroup(state, group)

// Is ANY of a release group's samples on a REJECTED batch/lot? Consulted FIRST by the release gate.
export function selectGroupBatchRejected(state, group) {
  if (!state.limsBatchHold) return false
  return batchLotsForGroup(state, group).some((lot) => limsBatchHold.selectIsBatchRejected(state, lot))
}

// Is a SPECIFIC sample's batch/lot rejected? The Sample View uses this to mark the exact
// sample-analysis that triggered the block with a STOP icon (SPEC-010).
export function selectIsSampleBatchRejected(state, sampleId) {
  if (!sampleId || !state.limsBatchHold || !state.limsSampleQc) return false
  const qc = state.limsSampleQc[sampleId]
  return !!(qc && qc.batchLot && limsBatchHold.selectIsBatchRejected(state, qc.batchLot))
}

// SPEC-018 (PRD-005 Area 4): does ANY sample in a release group have an OPEN (not-yet-closed) OOS?
// Consulted by the gate AFTER the batch-reject check to return the RECOVERABLE ON_HOLD.
// FE SPEC-048 (F4): counts ANY non-closed OOS — INCLUDING a phase1-null "raise" — via
// limsOos.selectOpenOosForSample, the exact mirror of the backend release gate's
// has_open_investigation (BE SPEC-048 R5 widened the server side; the old phase1-only filter here
// showed "Ready to release" for a group the server would refuse, and the signed submit died on a
// generic 422). Clearing the hold is a signed QA action either way (close/decide) — a raise has no
// lightweight unsigned withdraw (accepted, BE SPEC-049 F12).
export function selectGroupHasOpenInvestigation(state, group) {
  if (!state.limsOos) return false
  const analyses = (group && group.analyses) || []
  return analyses.some((a) => a.sampleId && limsOos.selectOpenOosForSample(state, a.sampleId).length > 0)
}

// Derive a sample row's release status from its analyses' QC results and whether it has already
// been released. SPEC-013 (A3): READY requires EVERY analysis to be QA-APPROVED (not merely
// reviewer-signed) — release means strictly "all QA-approved".
//
// Order of precedence (a sample can satisfy several conditions; the first match wins):
//   1. RELEASED      — already released (terminal, top of the list so a released CoA always shows).
//   2. BATCH_REJECTED — a signed QA STOP on the lot: TERMINAL, non-recoverable (checked before the
//      OOS hold so a rejected batch shows its own distinct, stronger status).
//   3. ON_HOLD       — ANY non-closed OOS on a sample in the group (incl. a phase1-null raise —
//      FE SPEC-048 / BE SPEC-048 R5): RECOVERABLE. The QC guide forbids releasing a lot while an
//      OOS is open; closing/deciding it (a QA signature) lifts the hold and the gate re-evaluates.
//   4. READY / IN_REVIEW / IN_PROGRESS — the normal QA-approval progression.
export function selectSampleReleaseStatus(state, group) {
  if (selectRelease(state, group)) return RELEASE_STATUS.RELEASED
  // SPEC-010: a signed batch rejection is a terminal, non-releasable block — checked FIRST so it
  // takes precedence and shows its own distinct status.
  if (selectGroupBatchRejected(state, group)) return RELEASE_STATUS.BATCH_REJECTED
  // SPEC-018 / FE SPEC-048: ANY non-closed OOS is a RECOVERABLE hold — checked after the terminal
  // batch-reject so a rejected batch keeps its stronger status, but before the QA-approval tally so
  // an open OOS blocks release even if results are already QA-approved (mirrors the server gate).
  if (selectGroupHasOpenInvestigation(state, group)) return RELEASE_STATUS.ON_HOLD
  const analyses = (group && group.analyses) || []
  if (analyses.length === 0) return RELEASE_STATUS.IN_PROGRESS
  // Match a sample's QC result by sampleId alone — a sampleId is unique to one (analysis,
  // observation) pairing, so it is a safe join and is robust to any analysisId field mismatch
  // between the sample-view payload and the result records.
  let qaApproved = 0
  let inReview = 0
  analyses.forEach((a) => {
    const r = state.limsResults.items.find((x) => x.sampleId === a.sampleId)
    // SPEC-013: only a QA-APPROVED result counts toward READY. A result still in the review
    // pipeline (in-review, sent-to-supervisor [SPEC-035 / F3], or sent-to-qa) keeps the sample
    // short of release.
    if (r && isQaApproved(r)) qaApproved += 1
    else if (r && (r.state === "in-review" || r.state === "sent-to-supervisor" || r.state === "sent-to-qa"))
      inReview += 1
  })
  if (qaApproved === analyses.length) return RELEASE_STATUS.READY
  if (inReview > 0) return RELEASE_STATUS.IN_REVIEW
  return RELEASE_STATUS.IN_PROGRESS
}
